'use client'

import { getDateTimePart } from '@/helpers/utils'

import AppliedFilterSection from '../../shared/AppliedFilterSection'
import type { AppliedProps } from '../../types'

const label = 'Filters.endTimeRange'

const formatBound = (iso: string): string =>
  new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso))

// Abbreviation for the browser's local timezone (e.g. "CDT"), shown once at
// the end of the range rather than after each bound.
const formatTimeZoneAbbreviation = (iso: string): string | undefined =>
  getDateTimePart(
    new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(
      new Date(iso)
    ),
    'timeZoneName'
  )

/**
 * `endTimeRange` has no sidebar picker of its own — populated only via
 * deep-link (e.g. the Auction Schedule click-through). `maxEndTime` is
 * inclusive up to `:59:59.999` of its hour; +1ms rounds the *displayed*
 * label up to a clean hour boundary (e.g. "2:00 PM – 3:00 PM") without
 * altering the actual filter bound sent to the search API.
 */
const EndTimeRangeFilterApplied = ({
  filtersState,
  updateFilters,
  noClose,
}: AppliedProps) => {
  const [start, end] = filtersState.endTimeRange
  if (!start && !end) return null

  const startLabel = start ? formatBound(start) : null
  const endLabel = end
    ? formatBound(new Date(new Date(end).getTime() + 1).toISOString())
    : null
  const timeZoneAbbreviation = formatTimeZoneAbbreviation(start ?? end ?? '')

  const range =
    startLabel && endLabel
      ? `${startLabel} – ${endLabel}`
      : (startLabel ?? endLabel ?? '')
  const name = timeZoneAbbreviation ? `${range} ${timeZoneAbbreviation}` : range

  return (
    <AppliedFilterSection
      label={label}
      values={[{ key: 'endTimeRange', name }]}
      onRemove={() => updateFilters({ endTimeRange: [null, null] })}
      noClose={noClose}
    />
  )
}

const EndTimeRangeFilter = {
  Applied: EndTimeRangeFilterApplied,
}

export default EndTimeRangeFilter
